import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { sectionColor } from '../../../lib/theme';

// Condensed single-line row: assessment + age group share one row so the
// per-player card stays compact (10+ teams over the years are typical for a
// multi-sport family). Padding intentionally tighter than the section-tile
// shape this file used to render.
const rowBase = `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  margin-top: 6px;
  border-radius: 6px;
`;

// Desktop: title — dash — age group, "Report card" pill, chevron — all on one
// row. Mobile: title + age group stack inside RowLabel (see below), and the
// Report card link wraps to its own row via flex: 1 1 100% as a tap-target
// button. flex-wrap on the link is what enables the wrap on mobile.
export const ProfileLink = styled(Link)`
  ${rowBase}
  border: 1px solid ${sectionColor};
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${sectionColor};
    border-color: #bdd739;
  }

  @media (max-width: 599px) {
    flex-wrap: wrap;
    padding: 12px;
    row-gap: 10px;
  }
`;

export const ProfileRowDisabled = styled.div`
  ${rowBase}
  border: 1px dashed ${sectionColor};
  opacity: 0.65;
`;

// "Report card" affordance. Desktop: small inline pill next to the chevron.
// Mobile: wraps to its own row via flex: 1 1 100% and reads as an obvious
// pressable button — coloured background, full label visible, centered.
export const ReportCardLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  color: inherit;
  background: transparent;
  border: 1px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 599px) {
    flex: 1 1 100%;
    order: 3;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    background: ${sectionColor};
    border-color: #bdd739;

    &:hover {
      background: #d6e873;
      border-color: #adb729;
    }
  }
`;

// Assessment + age group label. Desktop: one line, ellipsised on overflow.
// Mobile (≤599px): stacks as a column so the title gets its own line and the
// age group sits below it — much cleaner than the previous baseline-wrap that
// broke "2021 Training\nAssessment" mid-title.
//
// `flex: 1 1 0` on mobile (vs. desktop's `flex: 1 1 auto`) is what keeps the
// chevron glued to the right of the title row when the title wraps to a 2nd
// visual line. With `flex-basis: auto`, the column-stacked children's natural
// max-content width pushes RowLabel's basis past the available row width, and
// the flex algorithm wraps the chevron to its own row instead of shrinking the
// label. Forcing basis to 0 guarantees the chevron always has room on row 1
// and RowLabel grows to fill whatever's left.
export const RowLabel = styled.span`
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 599px) {
    flex: 1 1 0;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 2px;
    white-space: normal;
    overflow: visible;
  }
`;

export const RowPrimary = styled.span`
  font-weight: 600;
`;

// Separator between RowPrimary and RowSecondary in the inline desktop layout.
// Hidden on mobile where the two children stack vertically and the dash would
// dangle as a meaningless line. Kept as its own atom (vs. inlining "— " in
// JSX) so RowSecondary stays reusable for the "No access yet" disabled-row
// case where there is no preceding primary text.
export const RowDashSeparator = styled.span`
  color: rgba(0, 0, 0, 0.4);

  @media (max-width: 599px) {
    display: none;
  }
`;

export const RowSecondary = styled.span`
  font-weight: 400;
  color: rgba(0, 0, 0, 0.56);
`;
